import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaUsers, FaMapMarkerAlt, FaUtensils, FaComment, FaAngleDown } from 'react-icons/fa';
import { FaCalendarDays } from 'react-icons/fa6';

import axios from "axios";

// API endpoint for submitting the quote request
const API_PATH = 'https://classicfeast-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

// Regular expressions for validating email and phone number formats
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {

  // State for form input fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    eventType: '',
    eventDate: '',
    numberOfGuests: '',
    eventVenue: '',
    menuPreferences: '',
    comments: ''
  });

  // Form state tracking
  const [mailSent, setMailSent] = useState(false); // Success message visibility
  const [error, setError] = useState('');           // Error message content
  const [isSubmitting, setIsSubmitting] = useState(false); // Submission state

  // Automatically hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      // Validate each required field
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.eventType) validationErrors.push('Event type is required');
      if (!formData.eventDate) validationErrors.push('Event date is required');
      if (!formData.numberOfGuests) validationErrors.push('Number of guests is required');
      if (!formData.eventVenue) validationErrors.push('Event venue is required');
      if (!formData.menuPreferences) validationErrors.push('Menu preferences are required');
      if (!formData.comments) validationErrors.push('Additional comments are required');

      // Throw all validation errors together
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send form data via Axios to PHP backend
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Check if backend reports failure
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // Reset form after successful submission
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        eventType: '',
        eventDate: '',
        numberOfGuests: '',
        eventVenue: '',
        menuPreferences: '',
        comments: ''
      });
    } catch (err) {
      // Show error message from server or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle changes in input fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="custom-form get-quote linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Event Reservation'} subTitle={'Request a Quote'} linear sideHeading />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Your Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Event Type Select Field */}
              <div className="select">  
                <div className="select-container">
                  <label htmlFor="eventType"><FaCalendar /></label>
                  <select
                    className="select-box"
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Event Type</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Corporate">Corporate Event</option>
                    <option value="Birthday">Birthday Party</option>
                    <option value="Anniversary">Anniversary</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="input-box">

              {/* Event Date Input Field */}
              <div className="input-field"> 
                <label htmlFor="eventDate"><FaCalendarDays /></label>
                <input
                  type="text"
                  name="eventDate"
                  placeholder="Event Date"
                  id="eventDate"
                  className="box"
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Number of Guests Input Field */}
              <div className="input-field">
                <label htmlFor="numberOfGuests"><FaUsers /></label>
                <input
                  type="number"
                  name="numberOfGuests"
                  className="box"
                  id="numberOfGuests"
                  placeholder="Number of Guests"
                  value={formData.numberOfGuests}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Event Venue Input Field */}
              <div className="input-field">
                <label htmlFor="eventVenue"><FaMapMarkerAlt /></label>
                <input
                  type="text"
                  name="eventVenue"
                  className="box"
                  id="eventVenue"
                  placeholder="Event Venue"
                  value={formData.eventVenue}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Menu Preferences Select Field */}
              <div className="select">  
                <div className="select-container">
                  <label htmlFor="menuPreferences"><FaUtensils /></label>
                  <select
                    className="select-box"
                    id="menuPreferences"
                    name="menuPreferences"
                    value={formData.menuPreferences}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Menu Preferences</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Non-Vegetarian">Non-Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Gluten-Free">Gluten-Free</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Comments Text Area */}
            <div className="input-field text-area">
              <label htmlFor="comments"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="comments"
                className="box"
                id="comments"
                placeholder="Additional Comments"
                value={formData.comments}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}

            {/* Display Success Message if Form is Submitted */}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully</span></div>}

          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;